import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from './supabaseClient';
import PinLogin from './PinLogin';
import TaskRow from './TaskRow';
import TemplateManager from './TemplateManager';
import {
  DAY_NAMES, getWeekStart, weekKey, getDayDates, addWeeks,
  formatDate, makeWeekData, makeTask, makeTag, countTasks,
} from './utils';
import { THEME_KEY, getStoredTheme, themes } from './theme';
import { buildReportText } from './exportExcel';

const INDIA_PRICE_LABEL = process.env.REACT_APP_INDIA_PRICE_LABEL || 'Rs 149';
const INTERNATIONAL_PRICE_LABEL = process.env.REACT_APP_INTERNATIONAL_PRICE_LABEL || '$5';
const LIFETIME_PRICE = process.env.REACT_APP_PREMIUM_PRICE_LABEL || `${INDIA_PRICE_LABEL} / ${INTERNATIONAL_PRICE_LABEL}`;
const PAYMENT_SUPPORT_EMAIL = process.env.REACT_APP_PAYMENT_SUPPORT_EMAIL || 'kapoorashish213@gmail.com';
const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || '';
const ADSENSE_SLOT = process.env.REACT_APP_ADSENSE_SLOT || '';
const GUMROAD_PRODUCT_URL = process.env.REACT_APP_GUMROAD_PRODUCT_URL || 'https://ashishkapoor.gumroad.com/l/weeklyplanner';

const sixMonthsAgo = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 6);
  return getWeekStart(date);
};

function isLikelyIndiaVisitor() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language || ''];
    return timeZone === 'Asia/Kolkata'
      || timeZone === 'Asia/Calcutta'
      || languages.some(language => /-IN\b/i.test(language));
  } catch {
    return false;
  }
}

function encodeReportForUrl(report) {
  const bytes = new TextEncoder().encode(report);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

export default function App() {
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [allData, setAllData] = useState({});
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [access, setAccess] = useState({ loading: true, entitlement: null, missingSetup: false });
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => getWeekStart(new Date()));
  const [expandedDay, setExpandedDay] = useState(null);
  const [themeMode, setThemeMode] = useState(getStoredTheme);
  const [draggingTag, setDraggingTag] = useState(null);
  const [mobileDay, setMobileDay] = useState(DAY_NAMES[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]);
  const isMobile = useIsMobile();
  const draggingTagRef = useRef(null);
  const saveTimer = useRef(null);

  const user = session?.user;
  const today = new Date();
  const wk = weekKey(selectedWeekStart);
  const ws = getWeekStart(selectedWeekStart);
  const dayDates = getDayDates(ws);
  const historyWeekStart = useMemo(() => sixMonthsAgo(), []);
  const registeredWeekStart = user?.created_at ? getWeekStart(new Date(user.created_at)) : historyWeekStart;
  const minWeekStart = registeredWeekStart > historyWeekStart ? registeredWeekStart : historyWeekStart;
  const maxWeekStart = getWeekStart(today);
  const theme = themes[themeMode];
  const isLifetime = Boolean(access.entitlement?.lifetime_paid);
  const trialEndsAt = access.entitlement?.trial_ends_at ? new Date(access.entitlement.trial_ends_at) : null;
  const trialMsLeft = trialEndsAt ? trialEndsAt.getTime() - Date.now() : 0;
  const trialDaysLeft = trialMsLeft > 0 ? Math.max(1, Math.ceil(trialMsLeft / 86400000)) : 0;
  const hasActiveAccess = Boolean(user) || access.missingSetup;

  const weekOptions = useMemo(() => {
    const weeks = [];
    for (let d = getWeekStart(maxWeekStart); d >= minWeekStart; d = addWeeks(d, -1)) {
      weeks.push({
        key: weekKey(d),
        start: d,
        label: `${weekKey(d)} - ${formatDate(d)} to ${formatDate(addWeeks(d, 1) - 1)}`,
      });
    }
    return weeks;
  }, [maxWeekStart, minWeekStart]);

  const createWeekIfMissing = useCallback(async (weekId, weekStart) => {
    if (!user) return null;
    const weekData = makeWeekData(weekStart);
    const { data, error } = await supabase
      .from('planner_weeks')
      .insert({
        user_id: user.id,
        week_key: weekId,
        week_start: weekData.weekStart,
        data: weekData,
      })
      .select('data')
      .single();

    if (error) {
      if (error.code === '23505') {
        const { data: existing, error: existingError } = await supabase
          .from('planner_weeks')
          .select('data')
          .eq('user_id', user.id)
          .eq('week_key', weekId)
          .single();

        if (!existingError && existing?.data) return existing.data;
      }

      console.error('Week create error:', error);
      return null;
    }

    return data?.data || weekData;
  }, [user]);

  const saveWeek = useCallback((weekId, weekData) => {
    if (!user) return;
    clearTimeout(saveTimer.current);
    setSaving(true);
    saveTimer.current = setTimeout(async () => {
      try {
        await supabase.from('planner_weeks').upsert({
          user_id: user.id,
          week_key: weekId,
          week_start: weekData.weekStart,
          data: weekData,
        });
      } catch (err) {
        console.error('Save error:', err);
      }
      setSaving(false);
    }, 800);
  }, [user]);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setAuthReady(true);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(prevSession => {
        const prevUserId = prevSession?.user?.id;
        const nextUserId = nextSession?.user?.id;

        if (!nextUserId || (prevUserId && prevUserId !== nextUserId)) {
          setAllData({});
          setTemplates([]);
          setExpandedDay(null);
        }

        return nextSession;
      });
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    draggingTagRef.current = draggingTag;
  }, [draggingTag]);

  useEffect(() => {
    if (!draggingTag) return undefined;
    const clearDrag = () => setDraggingTag(null);
    const moveTagAtPointer = event => {
      const current = draggingTagRef.current;
      if (!current) return;

      const target = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest('[data-day-name][data-task-index]');
      if (!target) return;

      const targetDayName = target.getAttribute('data-day-name');
      const targetIndex = Number(target.getAttribute('data-task-index'));
      if (targetDayName !== current.dayName || !Number.isInteger(targetIndex) || targetIndex === current.index) return;

      setAllData(data => {
        const currentWeek = data[wk] || makeWeekData(ws);
        const dayData = currentWeek.days[current.dayName];
        if (!dayData?.tasks?.[current.index] || dayData.tasks[current.index].type !== 'tag') return data;

        const tasks = [...dayData.tasks];
        const [tag] = tasks.splice(current.index, 1);
        tasks.splice(targetIndex, 0, tag);

        const updatedWeek = {
          ...currentWeek,
          days: {
            ...currentWeek.days,
            [current.dayName]: {
              ...dayData,
              tasks,
            },
          },
        };

        draggingTagRef.current = { dayName: current.dayName, index: targetIndex };
        setDraggingTag(draggingTagRef.current);
        saveWeek(wk, updatedWeek);
        return { ...data, [wk]: updatedWeek };
      });
    };

    window.addEventListener('pointermove', moveTagAtPointer);
    window.addEventListener('mousemove', moveTagAtPointer);
    window.addEventListener('pointerup', clearDrag);
    window.addEventListener('mouseup', clearDrag);
    return () => {
      window.removeEventListener('pointermove', moveTagAtPointer);
      window.removeEventListener('mousemove', moveTagAtPointer);
      window.removeEventListener('pointerup', clearDrag);
      window.removeEventListener('mouseup', clearDrag);
    };
  }, [draggingTag, wk, ws, saveWeek]);

  useEffect(() => {
    if (!authReady) return;
    if (!user) {
      setAccess({ loading: false, entitlement: null, missingSetup: false });
      setLoading(false);
      return;
    }
    loadEntitlement();
  }, [authReady, user?.id]);

  useEffect(() => {
    if (!authReady || !user || access.loading) return;
    if (!hasActiveAccess) {
      setLoading(false);
      return;
    }
    loadData();
  }, [authReady, user?.id, access.loading, hasActiveAccess]);

  useEffect(() => {
    if (!user || !hasActiveAccess || loading || allData[wk]) return;
    let cancelled = false;
    createWeekIfMissing(wk, ws).then(createdWeek => {
      if (cancelled || !createdWeek) return;
      setAllData(data => data[wk] ? data : { ...data, [wk]: createdWeek });
    });
    return () => {
      cancelled = true;
    };
  }, [wk, user?.id, hasActiveAccess, loading, allData, createWeekIfMissing, ws]);

  async function loadEntitlement() {
    setAccess(current => ({ ...current, loading: true }));
    setPaymentError('');
    try {
      const { data, error } = await supabase
        .from('user_entitlements')
        .select('user_id, trial_starts_at, trial_ends_at, lifetime_paid, paid_at')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === '42P01' || error.code === 'PGRST205') {
          setAccess({ loading: false, entitlement: null, missingSetup: true });
          return;
        }
        throw error;
      }

      setAccess({ loading: false, entitlement: data, missingSetup: false });
    } catch (err) {
      console.error('Entitlement load error:', err);
      setPaymentError('Could not check account access. Try refreshing in a moment.');
      setAccess({ loading: false, entitlement: null, missingSetup: false });
    }
  }

  async function loadData() {
    setLoading(true);
    try {
      const { data: weeks, error: weeksError } = await supabase
        .from('planner_weeks')
        .select('*')
        .eq('user_id', user.id)
        .gte('week_start', minWeekStart.toISOString())
        .order('week_key', { ascending: false });

      if (weeksError) throw weeksError;

      const data = {};
      weeks?.forEach(row => {
        data[row.week_key] = row.data;
      });

      if (!data[wk]) {
        const createdWeek = await createWeekIfMissing(wk, ws);
        if (createdWeek) data[wk] = createdWeek;
      }

      setAllData(data);

      const { data: tplRows, error: tplError } = await supabase
        .from('planner_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (tplError) throw tplError;
      setTemplates(tplRows ? tplRows.map(r => r.data) : []);
    } catch (err) {
      console.error('Load error:', err);
    }
    setLoading(false);
  }

  function updateDay(dayName, dayData) {
    const currentWeek = allData[wk] || makeWeekData(ws);
    const updatedWeek = {
      ...currentWeek,
      days: { ...currentWeek.days, [dayName]: dayData },
    };
    const next = { ...allData, [wk]: updatedWeek };
    setAllData(next);
    saveWeek(wk, updatedWeek);
  }

  async function saveTemplate(tpl) {
    const next = templates.some(t => t.id === tpl.id)
      ? templates.map(t => t.id === tpl.id ? tpl : t)
      : [...templates, tpl];
    setTemplates(next);
    await supabase.from('planner_templates').upsert({ id: tpl.id, user_id: user.id, data: tpl });
  }

  async function deleteTemplate(id) {
    setTemplates(t => t.filter(x => x.id !== id));
    await supabase.from('planner_templates').delete().eq('id', id).eq('user_id', user.id);
  }

  function selectWeek(nextStart) {
    const next = getWeekStart(nextStart);
    if (next < minWeekStart || next > maxWeekStart) return;
    setSelectedWeekStart(next);
    setExpandedDay(null);
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  async function startPayment() {
    setPaymentBusy(true);
    setPaymentError('');
    try {
      const useInternationalCheckout = GUMROAD_PRODUCT_URL && !isLikelyIndiaVisitor();
      if (useInternationalCheckout) {
        const gumroadUrl = new URL(GUMROAD_PRODUCT_URL);
        if (user?.email) gumroadUrl.searchParams.set('email', user.email);
        gumroadUrl.searchParams.set('wanted', 'true');
        window.location.href = gumroadUrl.toString();
        return;
      }

      if (!isLikelyIndiaVisitor() && !GUMROAD_PRODUCT_URL) {
        throw new Error('International checkout is not ready yet. Please contact support.');
      }

      const { data, error } = await supabase.functions.invoke('create-payment-link', {
        body: { returnUrl: window.location.href },
      });
      if (error) throw error;
      const url = data?.short_url || data?.payment_link_url;
      if (!url) throw new Error('Payment link was not returned.');
      window.location.href = url;
    } catch (err) {
      console.error('Payment link error:', err);
      setPaymentError(err.message || 'Payment link could not be created. Please try again.');
    }
    setPaymentBusy(false);
  }

  function copyManagerReport() {
    const report = buildReportText([{ key: wk, label: `${formatDate(dayDates[0])} to ${formatDate(dayDates[6])}` }], allData);
    navigator.clipboard?.writeText(report).then(() => {
      setPaymentError('Weekly report copied. You can paste it into email, chat, or a manager update.');
    }).catch(() => {
      setPaymentError('Could not copy report. Try again from a supported browser.');
    });
  }

  function copyShareReportLink() {
    try {
      const report = buildReportText([{ key: wk, label: `${formatDate(dayDates[0])} to ${formatDate(dayDates[6])}` }], allData);
      const url = new URL(window.location.origin);
      url.searchParams.set('report', encodeReportForUrl(report));
      navigator.clipboard?.writeText(url.toString()).then(() => {
        setPaymentError('Read-only report link copied. Share it with a manager or client for review.');
      }).catch(() => {
        setPaymentError('Could not copy the report link. Try again from a supported browser.');
      });
    } catch (err) {
      console.error('Share report error:', err);
      setPaymentError('Could not create a shareable report link for this week.');
    }
  }

  function addCalendarReminder() {
    const start = new Date(ws);
    start.setDate(start.getDate() + 4);
    start.setHours(17, 0, 0, 0);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + 30);
    const toGoogleTime = date => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: 'Update Weekly Planner timesheet',
      dates: `${toGoogleTime(start)}/${toGoogleTime(end)}`,
      details: `Review tasks and hours in Weekly Planner: ${window.location.origin}`,
    });
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'noopener,noreferrer');
  }

  function toggleTheme() {
    setThemeMode(current => {
      const next = current === 'light' ? 'dark' : 'light';
      localStorage.setItem(THEME_KEY, next);
      return next;
    });
  }

  if (!authReady || access.loading || loading) return (
    <div style={{
      background: theme.bg, backgroundImage: theme.bgImage, minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Rajdhani', sans-serif", color: theme.primary, fontSize: 18,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 16, fontWeight: 800 }}>Weekly Planner</div>
        <div style={{ width: 38, height: 38, border: `3px solid ${theme.primarySoft}`, borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', margin: '0 auto', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  if (!user) return <PinLogin theme={theme} themeMode={themeMode} onToggleTheme={toggleTheme} />;

  if (!hasActiveAccess) {
    return (
      <Paywall
        user={user}
        entitlement={access.entitlement}
        theme={theme}
        themeMode={themeMode}
        price={LIFETIME_PRICE}
        supportEmail={PAYMENT_SUPPORT_EMAIL}
        busy={paymentBusy}
        error={paymentError}
        onPay={startPayment}
        onRefresh={loadEntitlement}
        onSignOut={signOut}
        onToggleTheme={toggleTheme}
      />
    );
  }

  const weekData = allData[wk] || makeWeekData(ws);
  const canGoBack = addWeeks(ws, -1) >= minWeekStart;
  const canGoForward = addWeeks(ws, 1) <= maxWeekStart;
  const visibleDays = isMobile ? [mobileDay] : DAY_NAMES;

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      backgroundImage: theme.bgImage,
      fontFamily: "'Rajdhani', 'DM Mono', sans-serif",
      color: theme.text,
    }}>
      <div style={{
        padding: isMobile ? '12px 10px' : '14px 20px',
        borderBottom: `1px solid ${theme.borderSoft}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: isMobile ? 10 : 16,
        position: 'sticky',
        top: 0,
        background: theme.glassStrong,
        backdropFilter: theme.glassBlur,
        WebkitBackdropFilter: theme.glassBlur,
        zIndex: 50,
        boxShadow: theme.mode === 'dark' ? '0 16px 45px rgba(0,0,0,0.32)' : '0 16px 45px rgba(36,55,99,0.10)',
      }}>
        <div style={{
          padding: '2px 0',
        }}>
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? 23 : 30,
            fontWeight: 900,
            color: theme.primary,
            fontFamily: "'Orbitron', sans-serif",
            textShadow: theme.mode === 'dark' ? '0 0 28px rgba(192,132,252,0.25)' : 'none',
          }}>
            Weekly Planner
          </h1>
          <div style={{ color: theme.muted, fontSize: 12, marginTop: 4, fontWeight: 700 }}>
            {user.email}
            {saving && <span style={{ color: theme.accent, marginLeft: 10, fontSize: 11 }}>saving...</span>}
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          justifyContent: isMobile ? 'stretch' : 'flex-end',
          width: isMobile ? '100%' : 'auto',
          padding: 6,
          borderRadius: 8,
          border: `1px solid ${theme.borderSoft}`,
          background: theme.glass,
          backdropFilter: theme.glassBlur,
          WebkitBackdropFilter: theme.glassBlur,
        }}>
          <button
            onClick={() => selectWeek(addWeeks(ws, -1))}
            disabled={!canGoBack}
            title="Previous week"
            style={navButton(!canGoBack, theme)}
          >◀</button>
          <select
            value={wk}
            onChange={e => {
              const selected = weekOptions.find(option => option.key === e.target.value);
              if (selected) selectWeek(selected.start);
            }}
            style={{
              minWidth: isMobile ? 0 : 240,
              flex: isMobile ? '1 1 180px' : '0 0 auto',
              maxWidth: '100%',
              padding: '8px 12px',
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              background: theme.surface,
              color: theme.text,
              fontSize: 14,
              fontFamily: 'inherit',
              fontWeight: 700,
              outline: 'none',
            }}
          >
            {weekOptions.map(option => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
          <button
            onClick={() => selectWeek(addWeeks(ws, 1))}
            disabled={!canGoForward}
            title="Next week"
            style={navButton(!canGoForward, theme)}
          >▶</button>
          <button onClick={toggleTheme} style={secondaryButton(theme)}>
            {themeMode === 'light' ? '◐ Dark' : '☀ Light'}
          </button>
          <button onClick={() => setShowTemplates(true)} style={primaryButton(theme)}>
            ◆ Templates
          </button>
          <button onClick={() => setShowHelp(true)} style={secondaryButton(theme)}>
            ? Help
          </button>
          <button onClick={() => setShowExport(true)} style={isLifetime ? primaryButton(theme) : secondaryButton(theme)}>
            Export Excel
          </button>
          <button onClick={copyManagerReport} style={secondaryButton(theme)}>
            Copy Report
          </button>
          <button onClick={copyShareReportLink} style={secondaryButton(theme)}>
            Share Link
          </button>
          <button onClick={addCalendarReminder} style={secondaryButton(theme)}>
            Calendar
          </button>
          {!access.missingSetup && (
            <AccessBadge
              isLifetime={isLifetime}
              trialDaysLeft={trialDaysLeft}
              busy={paymentBusy}
              theme={theme}
              price={LIFETIME_PRICE}
              onPay={startPayment}
            />
          )}
          <button onClick={signOut} style={secondaryButton(theme)}>
            ⏻ Sign out
          </button>
        </div>
      </div>

      <div style={{
        margin: isMobile ? '12px 10px 0' : '18px 22px 0',
        padding: '10px 14px',
        color: theme.text,
        fontSize: 13,
        background: theme.glassStrong,
        backdropFilter: theme.glassBlur,
        WebkitBackdropFilter: theme.glassBlur,
        border: `1px solid ${theme.borderSoft}`,
        borderRadius: 8,
        boxShadow: theme.shadow,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        fontWeight: 800,
      }}>
        <span style={{ color: theme.accent, fontFamily: "'Orbitron', sans-serif", fontSize: 11 }}>WEEK</span>
        <span>{formatDate(dayDates[0])} to {formatDate(dayDates[6])}</span>
        <span style={{ color: theme.faint }}>.</span>
        <span style={{ color: theme.primary }}>{wk}</span>
      </div>
      {isMobile && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 7,
          padding: '12px 10px 0',
        }}>
          {DAY_NAMES.map((dayName, i) => {
            const active = mobileDay === dayName;
            const isToday = dayDates[i].toDateString() === today.toDateString();
            const { done, total } = countTasks(weekData.days[dayName].tasks);
            return (
              <button
                key={dayName}
                type="button"
                onClick={() => {
                  setMobileDay(dayName);
                  setExpandedDay(dayName);
                }}
                style={{
                  minHeight: 54,
                  borderRadius: 9,
                  border: `1px solid ${active ? theme.primary : isToday ? theme.accent : theme.borderSoft}`,
                  background: active ? theme.primarySoft : theme.glassStrong,
                  backdropFilter: theme.glassBlur,
                  WebkitBackdropFilter: theme.glassBlur,
                  color: active ? theme.primary : theme.text,
                  fontFamily: "'Orbitron', sans-serif",
                  fontWeight: 900,
                  fontSize: 11,
                  cursor: 'pointer',
                  boxShadow: active ? theme.shadow : 'none',
                }}
              >
                <div>{dayName.slice(0, 3)}</div>
                <div style={{ marginTop: 4, color: theme.muted, fontFamily: "'Rajdhani', sans-serif", fontSize: 11 }}>
                  {done}/{total || 0}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {paymentError && (
        <div style={{
          margin: isMobile ? '10px 10px 0' : '10px 22px 0',
          padding: '9px 12px',
          display: 'inline-flex',
          borderRadius: 8,
          border: `1px solid ${theme.danger}`,
          background: theme.dangerSoft,
          color: theme.danger,
          fontWeight: 700,
          fontSize: 13,
        }}>
          {paymentError}
        </div>
      )}

      {!isLifetime && !access.missingSetup && (
        <AdSlot theme={theme} isMobile={isMobile} onUpgrade={startPayment} busy={paymentBusy} price={LIFETIME_PRICE} />
      )}

      <div style={{
        display: 'flex',
        gap: isMobile ? 0 : 14,
        padding: isMobile ? '12px 10px 18px' : '16px 14px 20px',
        overflowX: isMobile ? 'visible' : 'auto',
        alignItems: 'flex-start',
      }}>
        {visibleDays.map((dayName) => {
          const i = DAY_NAMES.indexOf(dayName);
          const isToday = dayDates[i].toDateString() === today.toDateString();
          const dayData = weekData.days[dayName];
          const { done, total } = countTasks(dayData.tasks);
          const pct = total > 0 ? Math.round(done / total * 100) : 0;
          const isExpanded = expandedDay === dayName;
          const moveDraggedTag = targetIndex => {
            if (!draggingTag || draggingTag.dayName !== dayName || draggingTag.index === targetIndex) return;
            const tasks = [...dayData.tasks];
            const [tag] = tasks.splice(draggingTag.index, 1);
            tasks.splice(targetIndex, 0, tag);
            setDraggingTag({ dayName, index: targetIndex });
            updateDay(dayName, { ...dayData, tasks });
          };

          return (
            <div key={dayName} style={{
              minWidth: isMobile ? '100%' : isExpanded ? 840 : 286,
              width: isMobile ? '100%' : isExpanded ? 840 : 286,
              flexShrink: 0,
              background: theme.glassStrong,
              border: '1px solid',
              borderColor: isToday ? theme.accent : theme.borderSoft,
              borderRadius: 8,
              overflow: 'hidden',
              boxShadow: isExpanded ? theme.shadowStrong : theme.shadow,
              transition: isMobile ? 'box-shadow 0.25s ease' : 'width 0.25s ease, min-width 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              backdropFilter: theme.glassBlur,
              WebkitBackdropFilter: theme.glassBlur,
            }}>
              <div style={{
                padding: '13px 13px 10px',
                background: isToday
                  ? `linear-gradient(135deg, ${theme.accentSoft}, ${theme.primarySoft})`
                  : theme.glassStrong,
                borderBottom: `1px solid ${theme.borderSoft}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 17,
                    fontWeight: 800,
                    color: isToday ? theme.accent : theme.text,
                    fontFamily: "'Orbitron', sans-serif",
                  }}>{dayName}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      color: theme.faint,
                      fontSize: 11,
                      padding: '3px 7px',
                      borderRadius: 8,
                      border: `1px solid ${theme.borderSoft}`,
                      background: theme.surface,
                    }}>{formatDate(dayData.date)}</span>
                    <button
                      onClick={() => {
                        const tasks = [makeTag(), ...dayData.tasks];
                        updateDay(dayName, { ...dayData, tasks });
                        setExpandedDay(dayName);
                      }}
                      title="Add tag to this day"
                      style={{
                        height: 26,
                        borderRadius: 6,
                        border: `1px solid ${theme.primary}`,
                        background: theme.primarySoft,
                        color: theme.primary,
                        cursor: 'pointer',
                        fontWeight: 900,
                        fontSize: 11,
                        fontFamily: "'Orbitron', sans-serif",
                        padding: '0 8px',
                      }}
                    >+ TAG</button>
                    <button
                      onClick={() => {
                        if (isMobile) {
                          setMobileDay(dayName);
                          setExpandedDay(dayName);
                        } else {
                          setExpandedDay(isExpanded ? null : dayName);
                        }
                      }}
                      title={isMobile ? 'Focused mobile day' : isExpanded ? 'Collapse day' : 'Expand day'}
                      style={{
                        width: 28,
                        height: 26,
                        borderRadius: 6,
                        border: `1px solid ${theme.primary}`,
                        background: isExpanded || isMobile ? theme.primary : theme.surface,
                        color: isExpanded || isMobile ? '#ffffff' : theme.primary,
                        cursor: 'pointer',
                        fontWeight: 800,
                        fontSize: 14,
                      }}
                    >{isExpanded ? '⤡' : '⤢'}</button>
                  </div>
                </div>
                {total > 0 && (
                  <div style={{ marginTop: 10, height: 5, background: theme.borderSoft, borderRadius: 999 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${theme.secondary}, ${theme.primary}, ${theme.accent})`, borderRadius: 999, transition: 'width 0.4s' }} />
                  </div>
                )}
              </div>

              <div style={{ padding: isMobile ? 8 : 10, maxHeight: isMobile ? 'none' : 620, overflowY: isMobile ? 'visible' : 'auto' }}>
                {dayData.tasks.map((task, idx) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    idx={idx}
                    dayName={dayName}
                    templates={templates}
                    theme={theme}
                    isMobile={isMobile}
                    onActivate={() => setExpandedDay(dayName)}
                    onDelete={() => {
                      const tasks = dayData.tasks.filter((_, taskIndex) => taskIndex !== idx);
                      updateDay(dayName, { ...dayData, tasks });
                    }}
                    onInsertTask={() => {
                      const tasks = [
                        ...dayData.tasks.slice(0, idx + 1),
                        makeTask(),
                        ...dayData.tasks.slice(idx + 1),
                      ];
                      updateDay(dayName, { ...dayData, tasks });
                    }}
                    onTagPointerDown={() => {
                      if (task.type !== 'tag') return;
                      setDraggingTag({ dayName, index: idx });
                      setExpandedDay(dayName);
                    }}
                    onTagPointerEnter={() => moveDraggedTag(idx)}
                    onTagPointerUp={() => setDraggingTag(null)}
                    isDraggingTag={draggingTag?.dayName === dayName && draggingTag?.index === idx}
                    onChange={updated => {
                      const tasks = dayData.tasks.map((t, taskIndex) => taskIndex === idx ? updated : t);
                      updateDay(dayName, { ...dayData, tasks });
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: isMobile ? '0 10px 16px' : '0 18px 16px', display: 'flex', gap: isMobile ? 9 : 18, flexWrap: 'wrap', color: theme.muted, fontSize: 11 }}>
        <span>▸ add / expand subtasks</span>
        <span>TAG divides each day into sections</span>
        <span>◆ templates apply saved task sets</span>
        <span>✦ notes hold task comments</span>
        <span style={{ color: theme.accent }}>Today is highlighted</span>
      </div>

      {showTemplates && (
        <TemplateManager
          templates={templates}
          onSave={saveTemplate}
          onDelete={deleteTemplate}
          onClose={() => setShowTemplates(false)}
          theme={theme}
        />
      )}
      {showHelp && (
        <HelpPanel
          theme={theme}
          onClose={() => setShowHelp(false)}
        />
      )}
      {showExport && (
        <ExportPanel
          theme={theme}
          weeks={weekOptions.filter(option => allData[option.key])}
          allData={allData}
          userEmail={user.email}
          isPremium={isLifetime}
          busy={paymentBusy}
          price={LIFETIME_PRICE}
          onPay={startPayment}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}

function AdSlot({ theme, isMobile, onUpgrade, busy, price }) {
  useEffect(() => {
    if (!ADSENSE_CLIENT || !ADSENSE_SLOT) return;
    const scriptId = 'weekly-planner-adsense';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
      document.head.appendChild(script);
    }
    window.adsbygoogle = window.adsbygoogle || [];
    try {
      window.adsbygoogle.push({});
    } catch (err) {
      console.warn('AdSense slot could not be requested yet.', err);
    }
  }, []);

  return (
    <div style={{
      margin: isMobile ? '12px 10px 0' : '14px 22px 0',
      padding: isMobile ? 12 : '12px 16px',
      borderRadius: 8,
      border: `1px dashed ${theme.border}`,
      background: theme.mode === 'dark' ? 'rgba(25,29,54,0.76)' : 'rgba(255,255,255,0.84)',
      color: theme.muted,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      boxShadow: theme.shadow,
    }}>
      {ADSENSE_CLIENT && ADSENSE_SLOT ? (
        <ins
          className="adsbygoogle"
          style={{ display: 'block', minHeight: isMobile ? 90 : 70, flex: '1 1 320px' }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={ADSENSE_SLOT}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      ) : (
      <div>
        <div style={{ color: theme.accent, fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 900 }}>
          Free plan
        </div>
        <div style={{ fontSize: 13, marginTop: 4 }}>
          This free version may show ads. Upgrade to remove ads and unlock Excel export.
        </div>
      </div>
      )}
      <button type="button" onClick={onUpgrade} disabled={busy} style={primaryButton(theme)}>
        {busy ? 'Opening...' : `Go Premium ${price}`}
      </button>
    </div>
  );
}

function ExportPanel({ theme, weeks, allData, userEmail, isPremium, busy, price, onPay, onClose }) {
  const [selected, setSelected] = useState(() => weeks.slice(0, 1).map(week => week.key));
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const selectedWeeks = weeks.filter(week => selected.includes(week.key));

  function toggleWeek(key) {
    setSelected(current => current.includes(key)
      ? current.filter(item => item !== key)
    : [...current, key]);
  }

  async function downloadPremiumExcel() {
    if (!isPremium || selectedWeeks.length === 0 || exporting) return;
    setExporting(true);
    setExportError('');
    try {
      const { data, error } = await supabase.functions.invoke('export-planner-weeks', {
        body: { weekKeys: selectedWeeks.map(week => week.key) },
      });
      if (error) throw error;
      if (!data?.base64 || !data?.filename) throw new Error('Export file was not returned.');

      const binary = atob(data.base64);
      const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
      const blob = new Blob([bytes], { type: data.contentType || 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export error:', err);
      setExportError('Could not create the premium Excel export. Please try again.');
    }
    setExporting(false);
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: theme.overlay,
      zIndex: 520,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 18,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 640,
        maxHeight: '84vh',
        overflowY: 'auto',
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowStrong,
        padding: 22,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: theme.primary, fontFamily: "'Orbitron', sans-serif", fontSize: 22 }}>
              Excel Export
            </h2>
            <p style={{ margin: '6px 0 0', color: theme.muted, fontSize: 13 }}>
              Select one or more weeks. Premium users get a formatted Excel file with summary, hours, notes, tags, and subtasks.
            </p>
          </div>
          <button onClick={onClose} style={secondaryButton(theme)}>Close</button>
        </div>

        {!isPremium && (
          <div style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 8,
            border: `1px solid ${theme.accent}`,
            background: theme.accentSoft,
            color: theme.text,
          }}>
            <strong style={{ color: theme.accent, fontFamily: "'Orbitron', sans-serif" }}>Premium feature</strong>
            <p style={{ margin: '6px 0 10px', color: theme.muted }}>
              Free users can track tasks with ads. Upgrade to remove ads and export selected weeks to Excel.
            </p>
            <button type="button" onClick={onPay} disabled={busy} style={primaryButton(theme)}>
              {busy ? 'Opening...' : `Unlock Premium ${price}`}
            </button>
          </div>
        )}

        <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
          {weeks.map(week => (
            <label key={week.key} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 10px',
              borderRadius: 8,
              border: `1px solid ${selected.includes(week.key) ? theme.primary : theme.borderSoft}`,
              background: selected.includes(week.key) ? theme.primarySoft : theme.surfaceSoft,
              cursor: 'pointer',
              color: theme.text,
              fontWeight: 800,
            }}>
              <input
                type="checkbox"
                checked={selected.includes(week.key)}
                onChange={() => toggleWeek(week.key)}
              />
              <span>{week.label}</span>
            </label>
          ))}
          {weeks.length === 0 && (
            <div style={{ color: theme.muted, padding: 12, border: `1px solid ${theme.borderSoft}`, borderRadius: 8 }}>
              No saved weeks are available yet.
            </div>
          )}
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={!isPremium || selectedWeeks.length === 0 || exporting}
            onClick={downloadPremiumExcel}
            style={{
              ...primaryButton(theme),
              opacity: !isPremium || selectedWeeks.length === 0 || exporting ? 0.55 : 1,
              cursor: !isPremium || selectedWeeks.length === 0 || exporting ? 'not-allowed' : 'pointer',
            }}
          >
            {exporting ? 'Preparing...' : 'Download Excel'}
          </button>
          <button type="button" onClick={() => setSelected(weeks.map(week => week.key))} style={secondaryButton(theme)}>
            Select All
          </button>
          <button type="button" onClick={() => setSelected([])} style={secondaryButton(theme)}>
            Clear
          </button>
        </div>
        {exportError && <div style={{ ...errorStyle(theme), marginTop: 12 }}>{exportError}</div>}
      </div>
    </div>
  );
}

function HelpPanel({ theme, onClose }) {
  const items = [
    'Use the week arrows or dropdown to view any week from your account start date.',
    'Click any task to expand that day. Use the day expand button to make a column wider.',
    'Type task text, mark it OK when done, and track hours with the small - / + controls.',
    'Open ... on a task to add subtasks, notes, or delete that task row.',
    'Use + TAG at the top of a day to create sections like Office, Personal, or House.',
    'Drag a tag by holding anywhere on the tag row. Double-click the tag to rename it.',
    'Use +TASK on a tag to add a task row under that section.',
    'On mobile, choose a day from the day chips to work in a clean single-day view without horizontal scrolling.',
    'Templates save reusable task/subtask sets and can be applied from task rows.',
    'Free users can keep using the planner with ads. Premium removes ads and unlocks Excel export.',
    'Use Excel Export to download selected weeks with task details, hours, notes, tags, and subtasks.',
    'Use Copy Report to paste a weekly summary into email, chat, or a manager update.',
    'Use Share Link to copy a read-only weekly report snapshot that managers or clients can open without logging in.',
    'Use Calendar to add a Google Calendar reminder for weekly timesheet review.',
    'Inactive unpaid accounts may be removed after 90 days to keep the service clean.',
    'Use Forgot password on the login page to receive a reset link by email.',
    'Light/Dark switches the planner theme. Your account and tasks stay synced through Supabase.',
  ];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: theme.overlay,
      zIndex: 500,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 18,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 620,
        maxHeight: '82vh',
        overflowY: 'auto',
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowStrong,
        padding: 22,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{
            margin: 0,
            color: theme.primary,
            fontFamily: "'Orbitron', sans-serif",
            fontSize: 22,
            fontWeight: 900,
          }}>
            Help
          </h2>
          <button onClick={onClose} style={secondaryButton(theme)}>Close</button>
        </div>
        <div style={{
          marginTop: 14,
          display: 'grid',
          gap: 9,
          color: theme.text,
          fontSize: 14,
          lineHeight: 1.45,
        }}>
          {items.map((item, index) => (
            <div key={item} style={{
              display: 'grid',
              gridTemplateColumns: '28px 1fr',
              gap: 8,
              alignItems: 'start',
              padding: '9px 10px',
              borderRadius: 8,
              background: index % 2 === 0 ? theme.surfaceSoft : theme.surfaceLift,
              border: `1px solid ${theme.borderSoft}`,
            }}>
              <span style={{
                color: theme.accent,
                fontWeight: 900,
                fontFamily: "'Orbitron', sans-serif",
              }}>{index + 1}</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= 760 : false
  ));

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 760);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return isMobile;
}

function AccessBadge({ isLifetime, trialDaysLeft, busy, theme, price, onPay }) {
  if (isLifetime) {
    return (
      <div style={{
        padding: '8px 11px',
        borderRadius: 8,
        border: `1px solid ${theme.success}`,
        background: theme.successSoft,
        color: theme.success,
        fontWeight: 900,
        fontSize: 13,
      }}>
        Premium unlocked
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: 4,
      borderRadius: 8,
      border: `1px solid ${theme.accent}`,
      background: theme.accentSoft,
    }}>
      <span style={{ color: theme.accent, fontWeight: 900, fontSize: 13, paddingLeft: 8 }}>
        Free with ads
      </span>
      <button
        type="button"
        onClick={onPay}
        disabled={busy}
        style={{
          ...primaryButton(theme),
          padding: '6px 10px',
          fontSize: 13,
          boxShadow: 'none',
          cursor: busy ? 'wait' : 'pointer',
        }}
      >
        {busy ? 'Opening...' : `Premium ${price}`}
      </button>
    </div>
  );
}

function Paywall({
  user,
  entitlement,
  theme,
  themeMode,
  price,
  supportEmail,
  busy,
  error,
  onPay,
  onRefresh,
  onSignOut,
  onToggleTheme,
}) {
  const trialEnds = entitlement?.trial_ends_at ? new Date(entitlement.trial_ends_at) : null;

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      backgroundImage: theme.bgImage,
      fontFamily: "'Rajdhani', 'DM Mono', sans-serif",
      color: theme.text,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 18,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 520,
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowStrong,
        padding: 26,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900, color: theme.primary, fontFamily: "'Orbitron', sans-serif" }}>
              Weekly Planner
            </h1>
            <div style={{ marginTop: 4, color: theme.muted, fontSize: 13 }}>{user.email}</div>
          </div>
          <button type="button" onClick={onToggleTheme} style={secondaryButton(theme)}>
            {themeMode === 'light' ? 'Dark' : 'Light'}
          </button>
        </div>

        <div style={{
          marginTop: 22,
          padding: 16,
          borderRadius: 8,
          background: theme.surfaceLift,
          border: `1px solid ${theme.borderSoft}`,
        }}>
          <div style={{ color: theme.accent, fontWeight: 900, fontFamily: "'Orbitron', sans-serif", fontSize: 18 }}>
            Trial ended
          </div>
          <p style={{ margin: '8px 0 0', color: theme.muted, lineHeight: 1.5 }}>
            Your 7 day trial is over. Unlock lifetime access to keep tracking tasks, subtasks, notes, tags, and hours.
          </p>
          {trialEnds && (
            <div style={{ marginTop: 10, color: theme.faint, fontSize: 12 }}>
              Trial ended on {trialEnds.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onPay}
          disabled={busy}
          style={{ ...primaryButton(theme), width: '100%', marginTop: 18, padding: '12px 14px', fontSize: 16 }}
        >
        {busy ? 'Creating secure checkout...' : `Unlock Premium for ${price}`}
        </button>

        <button
          type="button"
          onClick={onRefresh}
          style={{ ...secondaryButton(theme), width: '100%', marginTop: 10 }}
        >
          I have paid, refresh access
        </button>

        {error && <div style={errorStyle(theme)}>{error}</div>}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', color: theme.muted, fontSize: 12 }}>
          <span>Need help? {supportEmail}</span>
          <button type="button" onClick={onSignOut} style={{ background: 'none', border: 0, color: theme.primary, cursor: 'pointer', fontWeight: 800 }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function errorStyle(theme) {
  return {
    marginTop: 12,
    color: theme.danger,
    background: theme.dangerSoft,
    border: `1px solid ${theme.danger}`,
    borderRadius: 8,
    padding: '9px 11px',
    fontSize: 13,
  };
}

function navButton(disabled, theme) {
  return {
    width: 36,
    height: 36,
    borderRadius: 8,
    border: `1px solid ${disabled ? theme.borderSoft : theme.border}`,
    background: disabled ? theme.surfaceSoft : theme.surface,
    color: disabled ? theme.faint : theme.primary,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 15,
    fontWeight: 800,
    fontFamily: "'Orbitron', sans-serif",
  };
}

function primaryButton(theme) {
  return {
  padding: '9px 14px',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
  fontFamily: 'inherit',
  background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
  border: `1px solid ${theme.primary}`,
  color: '#ffffff',
  fontWeight: 700,
  boxShadow: theme.mode === 'dark' ? '0 12px 24px rgba(34,211,238,0.18)' : '0 12px 24px rgba(124,58,237,0.18)',
};
}

function secondaryButton(theme) {
  return {
  padding: '9px 12px',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
  fontFamily: 'inherit',
  background: theme.mode === 'dark' ? 'rgba(25,29,54,0.84)' : 'rgba(255,255,255,0.86)',
  border: `1px solid ${theme.border}`,
  color: theme.text,
  fontWeight: 700,
};
}
